import React, { useState } from "react";
import { Alert, Button, InputNumber, Modal, Segmented, Space, Typography } from "antd";
import { CarOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { isAuthenticated } from "../../../commons/Auth/auth";
import { sendOnTheWay } from "./api";
import { channelLabels, describeReason } from "./labels";
import "./notifications.css";

const PRESETS = [10, 20, 30, 45, 60];

/**
 * "Ihr Monteur ist unterwegs, Ankunft ca. 10:15": the technician tells the customer of an
 * appointment the expected arrival (SMS/WhatsApp, email as last resort).
 */
const OnTheWayButton = ({ appointmentId, block = false }) => {
  const { t } = useTranslation("dashboard");
  const channels = channelLabels(t);
  const [open, setOpen] = useState(false);
  const [minutes, setMinutes] = useState(20);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const arrival = new Date(Math.round((Date.now() + minutes * 60000) / 300000) * 300000);
  const arrivalText = arrival.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

  // Staff only (the calendar is shared with customers and admins)
  if (![1, 2].includes(Number(isAuthenticated()?.role))) return null;

  const send = async () => {
    setBusy(true);
    setResult(null);
    try {
      const res = await sendOnTheWay(appointmentId, { minutes });
      setResult({ ...res.data.data, duplicate: res.data.duplicate });
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setOpen(false);
    setResult(null);
  };

  return (
    <>
      <Button icon={<CarOutlined />} block={block} onClick={() => setOpen(true)}>
        {t("On the way")}
      </Button>
      <Modal
        rootClassName="notifications-page"
        open={open}
        title={t("Tell the customer you are on the way")}
        okText={t("Send")}
        cancelText={t("Close")}
        confirmLoading={busy}
        onOk={send}
        onCancel={close}
        okButtonProps={{ disabled: !minutes }}
      >
        <Typography.Paragraph>{t("Expected arrival in")}</Typography.Paragraph>
        <Space direction="vertical" className="nt-full">
          <Segmented
            block
            value={PRESETS.includes(minutes) ? minutes : "other"}
            onChange={value => value !== "other" && setMinutes(value)}
            options={[
              ...PRESETS.map(m => ({ value: m, label: `${m} min` })),
              { value: "other", label: t("Other") }
            ]}
          />
          <InputNumber
            className="nt-full"
            min={1}
            max={600}
            precision={0}
            value={minutes}
            onChange={value => setMinutes(value || 0)}
            addonAfter={t("minutes")}
          />
          <Typography.Text type="secondary">
            {t("Arrival shown to the customer: about {{time}}", { time: arrivalText })}
          </Typography.Text>
          {result && (
            <Alert
              showIcon
              type={
                result.status === "sent"
                  ? "success"
                  : result.status === "failed"
                    ? "error"
                    : "warning"
              }
              message={
                result.duplicate
                  ? t("This arrival time was already sent.")
                  : result.status === "sent"
                    ? t("Sent via {{channel}}.", {
                        channel: channels[result.channel] || result.channel
                      })
                    : result.status === "failed"
                      ? result.reason
                      : `${t("Not sent")}: ${describeReason(result.reason, t)}`
              }
            />
          )}
        </Space>
      </Modal>
    </>
  );
};

export default OnTheWayButton;
